import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { getIntlLocale } from '@/shared/i18n';
import { useSession } from '@/shared/session';
import { ErrorState, LoadingState } from '@/shared/ui';
import {
  AttachmentTile,
  ChannelBadge,
  ConversationStatusChip,
  DeliveryStatusIcon,
  EscalationReasonChip,
  formatClock,
  formatDayTime,
  IdleChip,
  MaskedPlaceholder,
  relativeTimeLabel,
} from '@/shared/ui/conversation';

import { resolveAttachmentContentUrl } from '../../api/conversationsApi';
import {
  conversationsKeys,
  useConversationLatestMessages,
  useCustomerConversation,
} from '../../hooks';
import { isPiiGatedAttachmentType } from '../../model/attachmentAccess';
import {
  effectiveText,
  isOperatorRow,
  operatorMetaLabel,
  resolveMessageBodyKind,
  resolveWhoLabel,
} from '../../model/messageDisplay';
import { staffLabel } from '../../model/staff';
import type { ConversationMessageView } from '../../model/types';

export interface CustomerConversationSummaryProps {
  customerId: string;
  /** 8 on the Customer 360 detail-page grid cell, 20 in the workspace tab (design §6 line 4). */
  messageLimit?: number;
  canViewPii: boolean;
  /**
   * Gates "Open conversation" — `conversations.view`, exactly what the inbox route itself requires
   * (design §6 line 6; reading the thread is not a write). The reply link below is the one that needs
   * `conversations.manage` + `conversations.feature.operator-reply` (`canReply`).
   */
  canOpenInbox: boolean;
  canReply: boolean;
}

function MessageRow({
  message,
  canViewPii,
  meId,
}: {
  message: ConversationMessageView;
  canViewPii: boolean;
  meId?: string | null;
}) {
  const { t } = useTranslation('conversations');
  const locale = getIntlLocale();
  const bodyKind = resolveMessageBodyKind(message, canViewPii);
  const firstAttachment = message.attachments?.[0];
  // Same operator-label + UNAVAILABLE-tooltip path as `MessageBubble`/`AsideCards` (design §13.2) — the
  // compact Customer 360 list explains WHY an operator row shows no name, not just that it doesn't.
  const whoLabel = isOperatorRow(message)
    ? operatorMetaLabel(message, meId, t)
    : { label: resolveWhoLabel(message, t, meId) };

  const body = (() => {
    if (bodyKind === 'deleted') {
      return (
        <Typography variant="body2" color="text.secondary" fontStyle="italic" noWrap>
          {t(message.direction === 'OUT' ? 'timeline.deletedOutbound' : 'timeline.deleted')}
        </Typography>
      );
    }
    if (bodyKind === 'masked') {
      return <MaskedPlaceholder kind={message.kind} />;
    }
    const text = effectiveText(message);
    return (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
        {message.kind === 'MEDIA' && firstAttachment ? (
          <AttachmentTile
            type={firstAttachment.type}
            storageStatus={firstAttachment.storageStatus}
            resolvedUrl={resolveAttachmentContentUrl(firstAttachment.contentUrl)}
            locked={isPiiGatedAttachmentType(firstAttachment.type) && !canViewPii}
            compact
          />
        ) : null}
        <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
          {text ?? ''}
        </Typography>
        {message.edited ? (
          <Typography variant="caption" color="text.secondary">
            {t('timeline.edited')}
          </Typography>
        ) : null}
      </Stack>
    );
  })();

  return (
    <ListItem disableGutters sx={{ py: 0.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
        <ChannelBadge channel={message.channel} size="small" variant="outlined" />
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          {formatClock(message.createdAt, locale)}
        </Typography>
        {whoLabel.tooltip ? (
          <Tooltip title={whoLabel.tooltip}>
            <Typography variant="caption" sx={{ fontWeight: 600, flexShrink: 0 }}>
              {whoLabel.label}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="caption" sx={{ fontWeight: 600, flexShrink: 0 }}>
            {whoLabel.label}
          </Typography>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>{body}</Box>
        {message.direction === 'OUT' ? (
          <DeliveryStatusIcon
            status={message.deliveryStatus}
            reason={message.deliveryReason}
            attempts={message.deliveryAttempts}
            channel={message.channel}
          />
        ) : null}
      </Stack>
    </ListItem>
  );
}

/**
 * The Customer 360 conversation summary (design-customer-360-conversation-tab.md §6/§11) — one
 * component, two hosts (`CustomerDetailPage`'s section, `CustomerWorkspacePage`'s tab). Read-only by
 * design (§11.3): no composer, no topic rename, no escalation actions — every action links into the
 * inbox, whose live polling and version-checked mutations this profile page does not replicate.
 */
export function CustomerConversationSummary({
  customerId,
  messageLimit = 8,
  canViewPii,
  canOpenInbox,
  canReply,
}: CustomerConversationSummaryProps) {
  const { t } = useTranslation('conversations');
  const { t: tCustomers } = useTranslation('customers');
  const { user } = useSession();
  const queryClient = useQueryClient();
  const locale = getIntlLocale();

  const conversation = useCustomerConversation(customerId, { poll: false });
  const detail = conversation.data;
  const messages = useConversationLatestMessages(detail?.id ?? null, {
    poll: false,
    enabled: Boolean(detail),
  });

  function refresh() {
    void queryClient.invalidateQueries({
      queryKey: conversationsKeys.customerConversation(customerId),
    });
    if (detail) {
      void queryClient.invalidateQueries({ queryKey: conversationsKeys.messagesLatest(detail.id) });
    }
  }

  if (conversation.isLoading) {
    return (
      <LoadingState fullHeight={false} label={tCustomers('detail.sections.conversation.loading')} />
    );
  }
  if (conversation.notFound) {
    return (
      <Stack spacing={0.5} sx={{ py: 1 }}>
        <Typography variant="subtitle2">
          {tCustomers('detail.sections.conversation.noneTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {tCustomers('detail.sections.conversation.noneDescription')}
        </Typography>
      </Stack>
    );
  }
  if (conversation.isError || !detail) {
    return (
      <ErrorState
        message={conversation.error?.message}
        onRetry={() => void conversation.refetch()}
      />
    );
  }

  const episode = detail.currentEpisode ?? null;
  const rows = [...(messages.data?.content ?? [])]
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt) || b.id.localeCompare(a.id))
    .slice(0, messageLimit);
  const canShowReplyLink = canReply && episode != null && episode.status !== 'CLOSED';

  return (
    <Stack spacing={1.25}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            fontStyle={episode?.topic ? undefined : 'italic'}
            color={episode?.topic ? 'text.primary' : 'text.secondary'}
          >
            {episode?.topic ?? tCustomers('detail.sections.conversation.noTopic')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {episode
              ? tCustomers('detail.sections.conversation.episodeStarted', {
                  time: formatDayTime(episode.startedAt, locale),
                  channel: t(`channel.${episode.openedByChannel}`, episode.openedByChannel),
                })
              : tCustomers('detail.sections.conversation.noActiveEpisode')}
            {episode
              ? ` · ${tCustomers('detail.sections.conversation.lastActivity', { value: relativeTimeLabel(episode.lastActivityAt, locale) })}`
              : ''}
            {typeof detail.episodesCount === 'number'
              ? ` · ${tCustomers('detail.sections.conversation.episodes', { count: detail.episodesCount })}`
              : ''}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <ConversationStatusChip status={episode?.status ?? detail.status} />
          {episode?.idle ? <IdleChip lastActivityAt={episode.lastActivityAt} /> : null}
          <Tooltip title={tCustomers('detail.sections.conversation.refresh')}>
            <IconButton
              size="small"
              aria-label={tCustomers('detail.sections.conversation.refresh')}
              onClick={refresh}
            >
              <RefreshRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {detail.sessions.map((session) => (
          <Chip
            key={session.id}
            size="small"
            variant="outlined"
            color={session.reachable ? 'success' : 'error'}
            icon={
              <ChannelBadge channel={session.channel} size="small" blocked={!session.reachable} />
            }
            label={t(session.reachable ? 'detail.sessions.reachable' : 'detail.sessions.blocked')}
          />
        ))}
      </Stack>

      {detail.openEscalation ? (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <EscalationReasonChip reason={detail.openEscalation.reason} />
          {detail.openEscalation.status === 'ACKED' ? (
            (() => {
              const acker = staffLabel(
                detail.openEscalation?.ackedByRef,
                detail.openEscalation?.ackedBy,
                user?.id ?? null,
                t,
              );
              const text = tCustomers('detail.sections.conversation.acknowledgedBy', {
                name: acker?.label ?? '—',
              });
              return acker?.tooltip ? (
                <Tooltip title={acker.tooltip}>
                  <Typography variant="body2" color="text.secondary">
                    {text}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {text}
                </Typography>
              );
            })()
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('detail.escalation.waiting', {
                duration: relativeTimeLabel(detail.openEscalation.requestedAt, locale),
              })}
            </Typography>
          )}
        </Stack>
      ) : null}

      {!canViewPii ? (
        <Alert
          severity="info"
          icon={<VisibilityOffRoundedIcon fontSize="inherit" />}
          role="status"
          sx={{ py: 0 }}
        >
          {tCustomers('detail.sections.conversation.piiNotice')}
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: 1 }}>
        <Typography variant="overline" color="text.secondary">
          {tCustomers('detail.sections.conversation.lastMessages')}
        </Typography>
        {messages.isLoading ? (
          <LoadingState fullHeight={false} label={t('timeline.loading')} />
        ) : messages.isError ? (
          <Alert
            severity="error"
            action={
              <Button onClick={() => void messages.refetch()}>
                {t('timeline.loadEarlierError')}
              </Button>
            }
          >
            {messages.error?.message}
          </Alert>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('timeline.empty')}
          </Typography>
        ) : (
          <List dense disablePadding>
            {rows.map((message) => (
              <MessageRow
                key={message.id}
                message={message}
                canViewPii={canViewPii}
                meId={user?.id}
              />
            ))}
          </List>
        )}
      </Paper>

      <Typography variant="caption" color="text.secondary">
        {tCustomers('detail.sections.conversation.openHint')}
      </Typography>
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        {canOpenInbox ? (
          <Button
            variant="outlined"
            component={RouterLink}
            to={`/conversations/${detail.id}`}
            startIcon={<ForumRoundedIcon />}
          >
            {tCustomers('detail.sections.conversation.open')}
          </Button>
        ) : null}
        {canShowReplyLink ? (
          <Button
            variant="contained"
            component={RouterLink}
            to={`/conversations/${detail.id}#reply`}
            endIcon={<OpenInNewRoundedIcon />}
          >
            {tCustomers('detail.sections.conversation.reply')}
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
}
